/**
 * Cloud provider enum and defaults resolver for the Evaluation > Performance tab.
 *
 * The deployed cloud environment is surfaced to the frontend via the
 * `VITE_CLOUD_TYPE` build-time env var ("AWS" | "GCP" | "AZURE"). When the
 * env var is missing or unrecognized we treat the deployment as LOCAL and
 * fall back to AWS-shaped defaults for the filter modal (AWS is also the
 * first option seeded in `performance_metrics`).
 */

export const CLOUD_PROVIDER = Object.freeze({
  AWS: 'AWS',
  GCP: 'GCP',
  AZURE: 'Azure',
  LOCAL: 'local',
});

const ENV_TO_ENUM = {
  AWS: CLOUD_PROVIDER.AWS,
  GCP: CLOUD_PROVIDER.GCP,
  AZURE: CLOUD_PROVIDER.AZURE,
};

/**
 * Resolve the active cloud provider from the `VITE_CLOUD_TYPE` env var.
 * Unknown / missing values map to `CLOUD_PROVIDER.LOCAL`.
 *
 * @returns {typeof CLOUD_PROVIDER[keyof typeof CLOUD_PROVIDER]}
 */
export function getCloudTypeFromEnv() {
  const raw = (import.meta.env.VITE_CLOUD_TYPE || '').toString().toUpperCase();
  return ENV_TO_ENUM[raw] || CLOUD_PROVIDER.LOCAL;
}

/**
 * Default `environments[0]` entry used to seed the Performance filters.
 * LOCAL and unknown clouds fall back to the AWS defaults so the filter
 * always points at a seeded combination. Values match what the
 * `performance_profiling.xlsx` workbook ships with.
 *
 * @returns {{ cloudProvider: string, hardwareDevice: string, accelerator: string|null, instanceType: string }}
 */
export function getDefaultEnvironment() {
  const cloud = getCloudTypeFromEnv();
  if (cloud === CLOUD_PROVIDER.GCP) {
    return {
      cloudProvider: CLOUD_PROVIDER.GCP,
      hardwareDevice: 'GPU',
      accelerator: 'T4',
      instanceType: 'n1-standard-8',
    };
  }
  if (cloud === CLOUD_PROVIDER.AZURE) {
    return {
      cloudProvider: CLOUD_PROVIDER.AZURE,
      hardwareDevice: 'GPU',
      accelerator: 'T4',
      instanceType: 'Standard_NC4as_T4_v3',
    };
  }
  return {
    cloudProvider: CLOUD_PROVIDER.AWS,
    hardwareDevice: 'GPU',
    accelerator: 'T4',
    instanceType: 'g4dn.xlarge',
  };
}

/**
 * Cloud providers shown in the filter modal. `LOCAL` is intentionally
 * excluded because `performance_metrics` has no local seeds.
 */
export const SELECTABLE_CLOUD_PROVIDERS = [
  { value: CLOUD_PROVIDER.AWS, label: 'AWS' },
  { value: CLOUD_PROVIDER.GCP, label: 'GCP' },
  { value: CLOUD_PROVIDER.AZURE, label: 'Azure' },
];
